import { useState, useEffect, cloneElement, type FocusEvent } from 'react';
import type {
  ICheckboxInternalProps,
  ICheckboxVariant,
  IRadioVariant,
  TCheckbox,
} from '../../types/checkbox';
import { Badge } from '../Badge';
import { CheckboxBase } from './CheckboxBase';
import styles from './checkbox.module.scss';

export const Checkbox = (props: TCheckbox) => {
  const {
    id,
    size = 'sm',
    type = 'checkbox',
    text,
    hintText,
    disabled = false,
    onChangeHandler,
    onClickHandler,
    badgeProps,
    checkboxAlignment = 'center',
    render
  } = props;
  const { checked, checkboxIcon } = props as ICheckboxVariant;
  const { selected } = props as IRadioVariant;
  const { hovered, focused } = props as ICheckboxInternalProps;

  const [isHovered, setIsHovered] = useState(hovered);
  const [isFocused, setIsFocused] = useState(focused);
  const [isChecked, setIsChecked] = useState(checked);

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  useEffect(() => {
    setIsHovered(hovered);
  }, [hovered]);

  useEffect(() => {
    setIsFocused(focused);
  }, [focused]);

  const onMouseOverHandler = () => {
    setIsHovered(true);
  };

  const onMouseOutHandler = () => {
    setIsHovered(false);
  };

  const onFocusHandler = (e: FocusEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsFocused(true);
  };

  const onBlurHandler = (e: FocusEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsFocused(false);
  };

  const handleClick = (e: any) => {
    e.preventDefault();
    const newChecked = !isChecked;
    setIsChecked(newChecked);
    onClickHandler?.(newChecked, (text || hintText) as string);
  };

  return (
    <div
      className={styles['checkbox-wrapper']}
      data-checkbox-disabled={disabled}
      data-checkbox-size={size}
      data-checkbox-alignment={checkboxAlignment}
      onClick={handleClick}
      onMouseOver={onMouseOverHandler}
      onMouseOut={onMouseOutHandler}
      onFocusCapture={onFocusHandler}
      onBlurCapture={onBlurHandler}
      tabIndex={0}
    >
      {cloneElement(
        <CheckboxBase
          id={id}
          size={size}
          icon={checkboxIcon}
          type={type}
          selected={selected}
          checked={isChecked}
          disabled={disabled}
          onChangeHandler={(checked) =>
            onChangeHandler?.(checked, text || hintText as string)
          }
        />,
        { hovered: isHovered, focused: isFocused }
      )}

      {!!render ? render : (text || hintText || badgeProps) && (
        <div className={styles['checkbox-wrapper__text-box']}>
          {(text || badgeProps) && (
            <div className={styles['checkbox-wrapper__text-box__row']}>
              {text && (
                <div className={styles['checkbox-wrapper__text-box__text']}>
                  {text}
                </div>
              )}
              {badgeProps && (
                <span className={styles['checkbox-wrapper__text-box__badge']}>
                  <Badge {...badgeProps} color={disabled ? 'gray' : badgeProps.color} />
                </span>
              )}
            </div>
          )}
          {hintText && (
            <div className={styles['checkbox-wrapper__text-box__hint-text']}>
              {hintText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
